import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Terminal, ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tokenValid = token.length >= 20;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      // Auto-login with the new credentials
      if (setSession) {
        setSession(data.token, data.user);
      } else {
        localStorage.setItem("dara_token", data.token);
      }
      toast.success("Password updated. You're in.");
      nav("/app");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed — please request a new link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md panel relative">
        <div className="border-b border-zinc-900 px-6 py-3 flex items-center gap-2">
          <Terminal className="w-4 h-4 text-[#FFB000]" />
          <span className="font-heading font-black tracking-tighter">DARA</span>
          <span className="font-mono text-[10px] text-zinc-500 ml-auto">AUTH://reset</span>
        </div>

        {!tokenValid ? (
          <div className="p-8 space-y-4" data-testid="reset-password-invalid-token">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#E63946]">// INVALID LINK</div>
            <h1 className="font-heading font-black text-3xl tracking-tighter uppercase">No reset token.</h1>
            <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">
              This page needs a token in the URL. Did you click the email link directly?
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-password-request-new-link"
              className="block text-center bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-[#E59E00] transition-colors mt-4"
            >
              Request new reset link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 space-y-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">// SET NEW PASSWORD</div>
              <h1 className="font-heading font-black text-3xl tracking-tighter uppercase">
                Choose a new<br />password.
              </h1>
              <p className="font-mono text-[11px] text-zinc-500 mt-3 leading-relaxed">
                Must be at least 8 characters. After saving, you&apos;ll be logged in automatically.
              </p>
            </div>

            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">New password</span>
              <input
                data-testid="reset-password-new-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Confirm new password</span>
              <input
                data-testid="reset-password-confirm-input"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </label>

            {error && (
              <div data-testid="reset-password-error" className="font-mono text-[11px] text-[#E63946] bg-[#E63946]/10 border border-[#E63946]/30 px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit-btn"
              className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider py-3 hover:bg-[#E59E00] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "UPDATING…" : <><Lock className="w-4 h-4" /> Update password <ArrowRight className="w-4 h-4" /></>}
            </button>

            <Link to="/login" className="block text-center font-mono text-[11px] text-zinc-500 hover:text-white pt-2" data-testid="reset-password-back-link">
              ← Back to login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
